/**
 * Book-level Codex entity graph (characters, locations, lore, items, custom).
 * Source of truth: `book.metadata.codex`.
 */

import type { Book } from "./models";
import type { BookCharacter } from "./characterRoster";
import type { BookLocation } from "./locationRoster";

export type CodexEntryType = "character" | "location" | "lore" | "item" | "custom";

export type CodexLink = {
  entryId: string;
  label: string;
};

export type CodexProgression = {
  label: string;
  note: string;
};

export type CodexEntry = {
  id: string;
  type: CodexEntryType;
  name: string;
  aliases: string[];
  summary: string;
  body: string;
  links: CodexLink[];
  seriesKey?: string;
  progressions?: CodexProgression[];
  updatedAt: string;
};

export type CodexState = {
  entries: CodexEntry[];
};

export const CODEX_ENTRY_TYPES: readonly CodexEntryType[] = [
  "character",
  "location",
  "lore",
  "item",
  "custom",
] as const;

export const CODEX_ENTRY_TYPE_LABELS: Record<CodexEntryType, string> = {
  character: "Character",
  location: "Location",
  lore: "Lore",
  item: "Item",
  custom: "Custom",
};

export function generateCodexEntryId(type: CodexEntryType = "custom"): string {
  return `codex-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function normalizeAliases(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== "string") continue;
    const alias = item.trim();
    if (!alias) continue;
    const key = alias.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(alias);
  }
  return out;
}

function normalizeLinks(raw: unknown): CodexLink[] {
  if (!Array.isArray(raw)) return [];
  const out: CodexLink[] = [];
  for (const item of raw) {
    const rec = asRecord(item);
    if (!rec) continue;
    const entryId = typeof rec.entryId === "string" ? rec.entryId.trim() : "";
    if (!entryId) continue;
    const label = typeof rec.label === "string" ? rec.label.trim() : "";
    out.push({ entryId, label });
  }
  return out;
}

function normalizeProgressions(raw: unknown): CodexProgression[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: CodexProgression[] = [];
  for (const item of raw) {
    const rec = asRecord(item);
    if (!rec) continue;
    const label = typeof rec.label === "string" ? rec.label.trim() : "";
    const note = typeof rec.note === "string" ? rec.note.trim() : "";
    if (!label && !note) continue;
    out.push({ label, note });
  }
  return out.length > 0 ? out : undefined;
}

export function normalizeCodexEntry(raw: unknown): CodexEntry | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const typeRaw = typeof rec.type === "string" ? rec.type.trim() : "custom";
  const type = (CODEX_ENTRY_TYPES as readonly string[]).includes(typeRaw)
    ? (typeRaw as CodexEntryType)
    : "custom";
  const id =
    typeof rec.id === "string" && rec.id.trim()
      ? rec.id.trim()
      : generateCodexEntryId(type);
  const seriesKey =
    typeof rec.seriesKey === "string" && rec.seriesKey.trim()
      ? rec.seriesKey.trim()
      : undefined;
  const progressions = normalizeProgressions(rec.progressions);
  return {
    id,
    type,
    name,
    aliases: normalizeAliases(rec.aliases),
    summary: typeof rec.summary === "string" ? rec.summary : "",
    body: typeof rec.body === "string" ? rec.body : "",
    links: normalizeLinks(rec.links),
    ...(seriesKey ? { seriesKey } : {}),
    ...(progressions ? { progressions } : {}),
    updatedAt:
      typeof rec.updatedAt === "string" && rec.updatedAt.trim()
        ? rec.updatedAt.trim()
        : nowIso(),
  };
}

export function normalizeCodexState(raw: unknown): CodexState {
  const rec = asRecord(raw);
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(rec?.entries)
      ? rec!.entries
      : [];
  const entries: CodexEntry[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    const entry = normalizeCodexEntry(item);
    if (!entry) continue;
    if (seen.has(entry.id)) continue;
    seen.add(entry.id);
    entries.push(entry);
  }
  return { entries };
}

export function emptyCodexState(): CodexState {
  return { entries: [] };
}

export function getCodex(book: { metadata?: Record<string, unknown> | null }): CodexState {
  return normalizeCodexState(book.metadata?.codex);
}

/** Ensure `book.metadata.codex` exists; returns the book (mutates metadata in place when missing). */
export function ensureCodexState(book: Book): Book {
  const meta = (book.metadata ?? {}) as Record<string, unknown>;
  if (!meta.codex) {
    meta.codex = emptyCodexState();
    book.metadata = meta;
  } else {
    meta.codex = normalizeCodexState(meta.codex);
    book.metadata = meta;
  }
  return book;
}

export function upsertCodexEntry(state: CodexState, entry: CodexEntry): CodexState {
  const normalized = normalizeCodexEntry({ ...entry, updatedAt: entry.updatedAt || nowIso() });
  if (!normalized) return state;
  const idx = state.entries.findIndex((e) => e.id === normalized.id);
  if (idx < 0) {
    return { entries: [...state.entries, normalized] };
  }
  const next = [...state.entries];
  next[idx] = { ...normalized, updatedAt: nowIso() };
  return { entries: next };
}

export function removeCodexEntry(state: CodexState, entryId: string): CodexState {
  const id = entryId.trim();
  if (!id) return state;
  return {
    entries: state.entries
      .filter((e) => e.id !== id)
      .map((e) => ({
        ...e,
        links: e.links.filter((l) => l.entryId !== id),
      })),
  };
}

export function linkCodexEntries(
  state: CodexState,
  fromId: string,
  toId: string,
  label = ""
): CodexState {
  const from = fromId.trim();
  const to = toId.trim();
  if (!from || !to || from === to) return state;
  const target = state.entries.find((e) => e.id === to);
  if (!target) return state;
  const linkLabel = label.trim() || target.name;
  return {
    entries: state.entries.map((e) => {
      if (e.id !== from) return e;
      if (e.links.some((l) => l.entryId === to)) {
        return {
          ...e,
          links: e.links.map((l) => (l.entryId === to ? { ...l, label: linkLabel } : l)),
          updatedAt: nowIso(),
        };
      }
      return {
        ...e,
        links: [...e.links, { entryId: to, label: linkLabel }],
        updatedAt: nowIso(),
      };
    }),
  };
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function entryMentionedInText(entry: CodexEntry, text: string): boolean {
  const hay = text.toLowerCase();
  const needles = [entry.name, ...entry.aliases]
    .map((n) => n.trim())
    .filter((n) => n.length >= 2);
  for (const needle of needles) {
    const re = new RegExp(`(?:^|[^\\p{L}\\p{N}_])${escapeRegExp(needle)}(?:$|[^\\p{L}\\p{N}_])`, "iu");
    if (re.test(text) || hay.includes(needle.toLowerCase())) return true;
  }
  return false;
}

/**
 * Build a short Codex snippet for AI injection.
 * When `chapterText` is set, prefer entries whose names/aliases appear in the text;
 * otherwise take the most recently updated entries (up to `limit`, default 12).
 */
export function buildCodexContextSnippet(
  book: { metadata?: Record<string, unknown> | null },
  opts?: { chapterText?: string; limit?: number }
): string {
  const limit = Math.max(1, Math.min(50, opts?.limit ?? 12));
  const { entries } = getCodex(book);
  if (entries.length === 0) return "";

  const chapterText = typeof opts?.chapterText === "string" ? opts.chapterText : "";
  let picked: CodexEntry[];
  if (chapterText.trim()) {
    const mentioned = entries.filter((e) => entryMentionedInText(e, chapterText));
    picked =
      mentioned.length > 0
        ? mentioned.slice(0, limit)
        : [...entries]
            .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
            .slice(0, limit);
  } else {
    picked = [...entries]
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .slice(0, limit);
  }

  const lines: string[] = ["## Codex"];
  for (const e of picked) {
    const aliasPart = e.aliases.length ? ` (aka ${e.aliases.join(", ")})` : "";
    const summary = e.summary.trim() || e.body.trim().slice(0, 160);
    lines.push(`- [${CODEX_ENTRY_TYPE_LABELS[e.type]}] ${e.name}${aliasPart}: ${summary || "(no summary)"}`);
  }
  return lines.join("\n");
}

function rosterSeedId(kind: "character" | "location", rosterId: string): string {
  return `codex-${kind}-${rosterId}`;
}

/**
 * Create/update Codex entries from character + location rosters.
 * Does not remove lore/item/custom entries or wipe existing body/links/progressions.
 */
export function seedCodexFromRosters(
  book: Book,
  characterRoster: BookCharacter[],
  locationRoster: BookLocation[]
): Book {
  ensureCodexState(book);
  let state = getCodex(book);

  for (const ch of characterRoster) {
    const id = rosterSeedId("character", ch.id);
    const existing = state.entries.find((e) => e.id === id)
      ?? state.entries.find(
        (e) => e.type === "character" && e.name.toLowerCase() === ch.name.trim().toLowerCase()
      );
    const base: CodexEntry = existing
      ? {
          ...existing,
          type: "character",
          name: ch.name.trim() || existing.name,
          aliases: ch.aliases.length ? [...ch.aliases] : existing.aliases,
          summary: ch.description?.trim() || existing.summary,
          // Preserve custom lore body / links / progressions
          body: existing.body,
          links: existing.links,
          progressions: existing.progressions,
          seriesKey: existing.seriesKey ?? ch.id,
          updatedAt: nowIso(),
        }
      : {
          id,
          type: "character",
          name: ch.name.trim(),
          aliases: [...ch.aliases],
          summary: ch.description?.trim() || "",
          body: "",
          links: [],
          seriesKey: ch.id,
          updatedAt: nowIso(),
        };
    if (!base.name) continue;
    state = upsertCodexEntry(state, base);
  }

  for (const loc of locationRoster) {
    const id = rosterSeedId("location", loc.id);
    const existing = state.entries.find((e) => e.id === id)
      ?? state.entries.find(
        (e) => e.type === "location" && e.name.toLowerCase() === loc.name.trim().toLowerCase()
      );
    const base: CodexEntry = existing
      ? {
          ...existing,
          type: "location",
          name: loc.name.trim() || existing.name,
          aliases: loc.aliases.length ? [...loc.aliases] : existing.aliases,
          summary: loc.description?.trim() || existing.summary,
          body: existing.body,
          links: existing.links,
          progressions: existing.progressions,
          seriesKey: existing.seriesKey ?? loc.id,
          updatedAt: nowIso(),
        }
      : {
          id,
          type: "location",
          name: loc.name.trim(),
          aliases: [...loc.aliases],
          summary: loc.description?.trim() || "",
          body: "",
          links: [],
          seriesKey: loc.id,
          updatedAt: nowIso(),
        };
    if (!base.name) continue;
    state = upsertCodexEntry(state, base);
  }

  book.metadata = {
    ...(book.metadata as Record<string, unknown>),
    codex: state,
  };
  return book;
}

export function createBlankCodexEntry(type: CodexEntryType = "custom"): CodexEntry {
  return {
    id: generateCodexEntryId(type),
    type,
    name: "Untitled",
    aliases: [],
    summary: "",
    body: "",
    links: [],
    updatedAt: nowIso(),
  };
}
